import { FormErrorMessages } from '@framework/form/field/FormErrorMessages.tsx';
import {
  type ColumnGroupType,
  type ColumnsType,
  type ColumnType,
} from 'antd/es/table';
import clx from 'classnames';
import { useMemo } from 'react';
import { RichTextAdvancedRender } from '../../../../../../inputs/rich-text-advanced/render/RichTextAdvancedRender.tsx';
import { getWidgetPath } from '../../../../../../validator/form-builder/getWidgetPath.ts';
import { useWidgetScopeContext } from '../../../../../form/fillable/WidgetScopeContext.tsx';
import { useBooleanConditions } from '../../../../hooks/useBooleanConditions.tsx';
import {
  type IWidgetOptionValue,
  type Widget,
  WidgetType,
} from '../../../../Widget.ts';
import { type IWidgetParsedProps } from '../../../inputs/common/util/getWidgetProps.ts';
import { isInputWidget } from '../../../inputs/isInputWidget.ts';
import { MultiSelectKind } from '../../../inputs/multi-select/IMultiSelectWidget.ts';
import { SingleSelectKind } from '../../../inputs/single-select/ISingleSelectWidget.ts';
import { materializeTableInputWidgets } from '../expression/materializeTableInputWidget.ts';
import { getTableWidgetAtCell } from '../getTableWidgetAtCell.tsx';
import { type ITableInputWidget } from '../ITableInputWidget.ts';
import {
  type ITableRow,
  type ITableRowErrorable,
  isCustomRow,
} from '../ITableRow.ts';
import { getTableInputColumnWidth } from './getTableInputColumnWidth.tsx';
import { getTableWidgetColumnTitle } from './getTableWidgetColumnTitle.tsx';
import {
  type IColumnGroupableWidget,
  TableColumnOptionComponent,
} from './TableColumnOptionComponent.tsx';
import { TableInputCellRender } from './TableInputCellRender.tsx';

export function useTableInputWidgetColumns(
  widget: ITableInputWidget,
  rows: ITableRowErrorable[],
  options: IWidgetParsedProps,
): ColumnsType<ITableRow> {
  const { prefix } = useWidgetScopeContext();

  const materializedWidgets = useMemo(
    () => materializeTableInputWidgets(widget),
    [widget],
  );

  /**
   * La visibilità delle colonne che calcoliamo qui è "globale", nel senso
   * che fa riferimento a tutte le righe della tabella; se ci sono virtualRefs
   * all'interno di una colonna, la visibilità di quella colonna è legata
   * alla singola riga, quindi non viene gestita qui.
   */
  const columnsVisibilites = useBooleanConditions(
    widget.columns.map(column => column.visible),
    { name: options.name, preview: options.preview },
  );

  const columns = useMemo(
    () =>
      getTableInputWidgetColumns(widget, {
        rows,
        options,
        materializedWidgets,
        columnsVisibilites,
        prefix,
      }),
    [widget, columnsVisibilites, options, materializedWidgets, rows, prefix],
  );

  return columns;
}

/**
 * Insieme di informazioni utili per la creazione delle colonne della tabella,
 * per semplificare il passaggio di argomenti.
 */
export interface ITableInputWidgetColumnsContext {
  options: IWidgetParsedProps;
  rows: ITableRowErrorable[];
  materializedWidgets: Widget[];
  columnsVisibilites: ReturnType<typeof useBooleanConditions>;
  prefix: string | null | undefined;
}

/**
 * Crea le colonne per la tabella, tenendo conto delle opzioni di visualizzazione
 * del widget, ad esempio il raggruppamento delle opzioni in gruppi.
 */
function getTableInputWidgetColumns(
  table: ITableInputWidget,
  context: ITableInputWidgetColumnsContext,
): ColumnsType<ITableRowErrorable> {
  const { columns } = table;

  const firstColumnWidth = getTableInputColumnWidth(
    table.layout?.rowsLabelColumnWidth,
  );

  return [
    // Prima colonna
    {
      className: clx('front-table-thead-item rows-header'),
      title: <RichTextAdvancedRender hasCustomFont text={table.label} />,
      dataIndex: 'rows',
      width: firstColumnWidth,
      key: 'rows',
      render: (_, record: ITableRow) => {
        return <RichTextAdvancedRender hasCustomFont text={record.label} />;
      },
    },

    ...columns
      .map((column, index) => {
        // Se la colonna è calcolata come invisibile, e non ci sono riferimenti
        // virtuali, non la renderizzo.
        // Se ci sono rif. virtuali, la visibilità è dinamica
        if (
          !context.columnsVisibilites[index].result &&
          !column.__hasVirtualColumnRefs
        )
          return null;

        if (
          column.type === WidgetType.MultiSelect &&
          column.kind === MultiSelectKind.Checkbox
        ) {
          return getColumnGroup(table, context, column, index);
        }

        if (
          column.type === WidgetType.Select &&
          column.kind === SingleSelectKind.Radio
        ) {
          const { options } = column;
          if (!options || options.length === 0) return {};
          return getColumnGroup(table, context, column, index);
        }

        const columnTitle = getTableWidgetColumnTitle(column);
        const columnWidth = getTableInputColumnWidth(
          column.layout?.tableColumnWidth,
        );

        return /* NOSONAR (mismatch) */ {
          className: clx('front-table-thead-item'),
          title: columnTitle,
          dataIndex: isInputWidget(column) ? column.code : undefined,
          key: index,
          align: 'center' as const,
          width: columnWidth,
          shouldCellUpdate: (record, prevRecord) => record !== prevRecord,
          render: (value: any, record: ITableRow, rowIndex: number) => {
            const widgetRowIndex = context.rows[rowIndex].isError
              ? rowIndex - 1
              : rowIndex;
            const cellWidget = getTableWidgetAtCell(
              table,
              context,
              widgetRowIndex,
              index,
            );

            // Riga di errore
            if (context.rows[rowIndex].isError && cellWidget) {
              return (
                <FormErrorMessages
                  name={getWidgetPath(cellWidget, context.prefix)}
                />
              );
            }

            return (
              <TableInputCellRender cellWidget={cellWidget} context={context} />
            );
          },
        } as ColumnType<ITableRow>;
      })
      .filter(isNonNullColumn),
  ];
}

const isNonNullColumn = (
  column: ColumnType<ITableRow> | ColumnGroupType<ITableRow> | null,
): column is ColumnType<ITableRow> | ColumnGroupType<ITableRow> =>
  column != null;

/**
 * Costruisce il columnGroup in base al widget. Per esempio i
 * campi di selezione in visualizzazione radio/checkbox richiedono
 * una colonna per ogni opzione.
 */
function getColumnGroup(
  table: ITableInputWidget,
  context: ITableInputWidgetColumnsContext,
  column: IColumnGroupableWidget,
  index: number,
): ColumnGroupType<ITableRow> | null {
  const { options: allOptions } = column;
  if (!allOptions || allOptions.length === 0) return null;

  const valueOptions = allOptions.filter(
    (option): option is IWidgetOptionValue => option.type === 'option',
  );

  const groupWidth = column.layout?.tableColumnWidth;
  const optionsWidth = groupWidth
    ? groupWidth / valueOptions.length
    : undefined;
  return {
    title: <RichTextAdvancedRender hasCustomFont text={column.label} />,
    key: index,
    className: 'front-table-thead-group',
    width: groupWidth ? `${groupWidth}%` : undefined,
    children: valueOptions.map((option, optionIndex) => {
      /**
       * Renderizza la cella custom solo se è la prima colonna delle opzioni.
       */
      const shouldRenderCustomCell = (rowIndex: number | undefined) =>
        rowIndex != null && isCustomRow(context.rows[rowIndex]);

      return {
        className: clx('front-table-thead-group-item', {
          'item-last-child': optionIndex === valueOptions.length - 1,
        }),
        key: optionIndex,
        width: getTableInputColumnWidth(optionsWidth),
        title: <RichTextAdvancedRender hasCustomFont text={option.label} />,

        /**
         * Nel caso di riga custom, la cella deve occupare tutte le colonne
         * delle opzioni.
         */
        onCell(widget, rowIndex) {
          if (
            shouldRenderCustomCell(rowIndex) ||
            context.rows[rowIndex ?? 0]?.isError
          ) {
            return {
              colSpan: optionIndex === 0 ? valueOptions.length : 0,
            };
          }

          return {};
        },

        dataIndex: option.value, //  option.type === 'option' ? option.value : option.label,
        align: 'center' as const,

        /**
         * In questo caso ci sono due possibilità:
         * 1. Si è in una riga normale, quindi si è sicuramente in un componente
         *    con opzioni (radio/checkbox) che presenta l'optionComponent
         * 2. Si è in una riga custom, quindi dobbiamo renderizzare
         *    un cellWidget normale (vedi `shouldRenderCustomCell`)
         */
        render: (value: any, record: ITableRow, rowIndex: number) => {
          const widgetRowIndex = context.rows[rowIndex].isError
            ? rowIndex - 1
            : rowIndex;
          const cellWidget = getTableWidgetAtCell(
            table,
            context,
            widgetRowIndex,
            index,
          );

          // Riga di errore
          if (context.rows[rowIndex].isError && cellWidget) {
            return (
              <FormErrorMessages
                name={getWidgetPath(cellWidget, context.prefix)}
              />
            );
          }

          if (shouldRenderCustomCell(rowIndex)) {
            // Nasconde le celle delle opzioni dopo la prima. Renderizza
            // solo la prima, con un `colSpan` che occupa tutte le colonne
            // (vedi `onCell`)
            if (optionIndex !== 0) return null;

            return (
              <TableInputCellRender cellWidget={cellWidget} context={context} />
            );
          }

          // Non dovrebbe mai succedere, perché se siamo in una riga NON custom,
          // la visualizzazione è sempre radio/checkbox e `getTableWidgetAtCell`
          // dovrebbe ritornare esattamente questo tipo di Widget.
          if (
            cellWidget?.type !== WidgetType.MultiSelect &&
            cellWidget?.type !== WidgetType.Select
          ) {
            // biome-ignore format: <tbd>
            console.warn(`[TableInputWidget] Widget ${cellWidget?.type as string} non supportato come Opzione.`);
            return null;
          }

          return (
            <TableColumnOptionComponent
              context={context}
              rowIndex={rowIndex}
              cellWidget={cellWidget}
              optionIndex={optionIndex}
              index={index}
              column={column}
              option={option}
              table={table}
            />
          );
        },
      };
    }),
  };
}
